// Rebuild merged Tandem transcript bubbles from two persisted Chat instances (instructor + engineer).
// Order matches live generation by **merging** each lane's non-system messages (already chronological)
// using segment timestamps when present, with a stable fallback so user rows without timestamps
// do not sort after every assistant message.

import {
  TANDEM_LANES,
  TANDEM_SPEAKERS,
} from "../../../systems/core/instances/tandem/base/TandemBubbleUiSystem.js";
import { reconstructMessageBubbles } from "../../../systems/core/utils/chatRestorationBinder.js";
import { resolveToolBubbleStyle } from "../../../systems/core/utils/toolBubbleStyle.js";

/**
 * Earliest segment time on a message, or Infinity if none.
 * @param {*} message
 * @returns {number}
 */
function minSegmentTimeMs(message) {
  const segs = message?.data?.segments || [];
  let minT = Number.POSITIVE_INFINITY;
  for (const s of segs) {
    const ts = s?.startTimestamp || s?.endTimestamp;
    if (ts) {
      const n = new Date(ts).getTime();
      if (!Number.isNaN(n)) minT = Math.min(minT, n);
    }
  }
  return minT;
}

/**
 * Comparable sort key for merge step: real segment time when present, otherwise
 * `chat.createdAt` + small per-index epsilon so within-lane `messageIDs` order is preserved.
 *
 * @param {import("../../../systems/core/chat/Chat.js").Chat} chat
 * @param {string} messageId
 * @param {number} indexInLane — 0-based index among non-system messages in that lane
 * @returns {number}
 */
function mergeSortKey(chat, messageId, indexInLane) {
  const msg = chat?.messages?.get?.(messageId) ?? chat?.getMessage?.(messageId);
  const t = minSegmentTimeMs(msg);
  if (Number.isFinite(t) && t < Number.POSITIVE_INFINITY) {
    return t + indexInLane * 1e-6;
  }
  const base =
    typeof chat?.createdAt === "number" && !Number.isNaN(chat.createdAt)
      ? chat.createdAt
      : Date.now();
  return base + indexInLane * 100 + 1e-3;
}

function listNonSystemMessageIds(chat) {
  const ids = Array.isArray(chat?.messageIDs) ? chat.messageIDs : [];
  return ids.filter((id) => {
    const m = chat.messages?.get?.(id) ?? chat.getMessage?.(id);
    return m && m.data?.role && m.data.role !== "system";
  });
}

/**
 * Merge two per-lane chronological lists into one timeline (same strategy as
 * merging sorted arrays).
 *
 * @param {import("../../../systems/core/chat/Chat.js").Chat} chatInstructor
 * @param {import("../../../systems/core/chat/Chat.js").Chat} chatEngineer
 */
function buildMergedReplayOrder(chatInstructor, chatEngineer) {
  const idsI = listNonSystemMessageIds(chatInstructor);
  const idsE = listNonSystemMessageIds(chatEngineer);
  let i = 0;
  let j = 0;
  const out = [];

  while (i < idsI.length || j < idsE.length) {
    if (i >= idsI.length) {
      const id = idsE[j];
      out.push({
        lane: TANDEM_LANES.ENGINEER,
        messageId: id,
        message: chatEngineer.messages.get(id),
      });
      j += 1;
      continue;
    }
    if (j >= idsE.length) {
      const id = idsI[i];
      out.push({
        lane: TANDEM_LANES.INSTRUCTOR,
        messageId: id,
        message: chatInstructor.messages.get(id),
      });
      i += 1;
      continue;
    }

    const ki = mergeSortKey(chatInstructor, idsI[i], i);
    const kj = mergeSortKey(chatEngineer, idsE[j], j);

    if (ki < kj) {
      const id = idsI[i];
      out.push({
        lane: TANDEM_LANES.INSTRUCTOR,
        messageId: id,
        message: chatInstructor.messages.get(id),
      });
      i += 1;
    } else if (kj < ki) {
      const id = idsE[j];
      out.push({
        lane: TANDEM_LANES.ENGINEER,
        messageId: id,
        message: chatEngineer.messages.get(id),
      });
      j += 1;
    } else {
      // Same timestamp: prefer instructor lane (human/instructor side usually leads the turn).
      const id = idsI[i];
      out.push({
        lane: TANDEM_LANES.INSTRUCTOR,
        messageId: id,
        message: chatInstructor.messages.get(id),
      });
      i += 1;
    }
  }

  return out;
}

function applyLaneStamping(tandemBubbleUiSystem, lane, message) {
  if (!tandemBubbleUiSystem) return;
  tandemBubbleUiSystem.setActiveLane(lane);
  const role = message?.data?.role;
  if (lane === TANDEM_LANES.INSTRUCTOR && role === "user") {
    tandemBubbleUiSystem.setPendingUserSpeakerOverride(null);
    tandemBubbleUiSystem.setActiveAssistantSpeaker(TANDEM_SPEAKERS.INSTRUCTOR);
  } else if (lane === TANDEM_LANES.ENGINEER && role === "user") {
    tandemBubbleUiSystem.setPendingUserSpeakerOverride(TANDEM_SPEAKERS.INSTRUCTOR);
    tandemBubbleUiSystem.setActiveAssistantSpeaker(TANDEM_SPEAKERS.ENGINEER);
  } else {
    tandemBubbleUiSystem.setPendingUserSpeakerOverride(null);
    tandemBubbleUiSystem.setActiveAssistantSpeaker(
      lane === TANDEM_LANES.INSTRUCTOR
        ? TANDEM_SPEAKERS.INSTRUCTOR
        : TANDEM_SPEAKERS.ENGINEER
    );
  }
}

/**
 * Clears the bubble host and replays instructor + engineer messages in **true turn order**
 * (merge of two lane timelines) with correct Tandem speaker / lane stamping.
 *
 * @param {Object} opts
 * @param {import("../../../systems/core/chat/Chat.js").Chat} opts.chatInstructor
 * @param {import("../../../systems/core/chat/Chat.js").Chat} opts.chatEngineer
 * @param {import("../../../systems/core/ui/BubbleUiSystem.js").BubbleUiSystem} opts.chatArea
 * @param {*} opts.aiToolSystem
 * @param {import("../../../systems/core/instances/tandem/base/TandemBubbleUiSystem.js").TandemBubbleUiSystem} [opts.tandemBubbleUiSystem]
 * @param {(args: { messageId: string, bubbleIds: string[], lastBubbleId: string|null }) => void} [opts.onLinkUserMessageBubbles]
 */
export function reconstructTandemMergedChatBubbles({
  chatInstructor,
  chatEngineer,
  chatArea,
  aiToolSystem,
  tandemBubbleUiSystem = null,
  onLinkUserMessageBubbles,
}) {
  if (!chatArea?.createBubble) {
    return;
  }

  if (chatArea.clearBubbles) {
    chatArea.clearBubbles();
  }

  const items = buildMergedReplayOrder(chatInstructor, chatEngineer);

  for (const { lane, messageId, message } of items) {
    if (!message) continue;
    applyLaneStamping(tandemBubbleUiSystem, lane, message);
    reconstructMessageBubbles({
      message,
      messageId,
      chatArea,
      aiToolSystem,
      onLinkUserMessageBubbles,
      getBubbleStyle: (fn) => resolveToolBubbleStyle(fn, aiToolSystem, null),
    });
    if (tandemBubbleUiSystem) {
      tandemBubbleUiSystem.setPendingUserSpeakerOverride(null);
    }
  }
}
